import { Skeleton } from "@mantine/core";
import { cbc } from "@noble/ciphers/aes.js";
import { md5 } from "@noble/hashes/legacy.js";
import { EncryptStorage } from "encrypt-storage";
import { Navigate, useLocation } from "react-router";

import { env } from "@/config/env";
import { paths } from "@/config/paths";
import { useUser } from "@/services/auth";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { AuthResponse } from "@/types/auth";
import type { FC, PropsWithChildren } from "react";

const key = `${env.STORAGE_PREFIX}token`;
const migrationKey = `${key}:v3-migration`;

const storage = EncryptStorage.create(env.STORAGE_SECRET_KEY, {
  engine: "noble",
  storageType: "localStorage",
});

// Validates that a decrypted value has the expected authentication token structure.
function isAuthResponse(value: unknown): value is AuthResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "access_token" in value &&
    typeof value.access_token === "string" &&
    "access_token_expires" in value &&
    typeof value.access_token_expires === "string" &&
    "hostname" in value &&
    typeof value.hostname === "string"
  );
}

// Decrypts values created by encrypt-storage v2.
// v2 used CryptoJS-compatible AES-CBC encryption with an OpenSSL-style salted payload.
function decryptV2Value(value: string): AuthResponse | undefined {
  try {
    const payload = Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
    const header = [0x53, 0x61, 0x6c, 0x74, 0x65, 0x64, 0x5f, 0x5f];

    if (
      payload.length < 32 ||
      header.some((byte, index) => payload[index] !== byte) ||
      (payload.length - 16) % 16 !== 0
    ) {
      return undefined;
    }

    const password = new TextEncoder().encode(env.STORAGE_SECRET_KEY);
    const salt = payload.subarray(8, 16);
    const keyAndIv = new Uint8Array(48);
    let previous = new Uint8Array(0);
    let offset = 0;

    // Recreates the key and IV derivation used by the v2 encryption format.
    while (offset < keyAndIv.length) {
      const input = new Uint8Array(previous.length + password.length + salt.length);
      input.set(previous);
      input.set(password, previous.length);
      input.set(salt, previous.length + password.length);
      previous = md5(input);
      const length = Math.min(previous.length, keyAndIv.length - offset);
      keyAndIv.set(previous.subarray(0, length), offset);
      offset += length;
    }

    const plaintext = cbc(keyAndIv.subarray(0, 32), keyAndIv.subarray(32, 48)).decrypt(
      payload.subarray(16)
    );
    const token: unknown = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(plaintext));

    return isAuthResponse(token) ? token : undefined;
  } catch {
    return undefined;
  }
}

// Compares the relevant authentication fields to verify that a migrated token
// contains the same data as the original v2 token.
function hasSameAuthResponse(first: AuthResponse, second: AuthResponse) {
  return (
    first.access_token === second.access_token &&
    first.access_token_expires === second.access_token_expires &&
    first.hostname === second.hostname
  );
}

// Migrates a decrypted v2 token to the v3 storage format.
// The token is first staged and verified using the v3 storage before replacing
// the original value, with the v2 ciphertext restored if the migration fails.
function migrateV2Value(token: AuthResponse, encryptedValue: string): AuthResponse {
  try {
    storage.setItem(migrationKey, token);
    const stagedToken = storage.getItem<AuthResponse>(migrationKey);

    if (!isAuthResponse(stagedToken) || !hasSameAuthResponse(stagedToken, token)) {
      storage.removeItem(migrationKey);
      return token;
    }

    storage.setItem(key, token);
    const migratedToken = storage.getItem<AuthResponse>(key);

    if (isAuthResponse(migratedToken) && hasSameAuthResponse(migratedToken, token)) {
      storage.removeItem(migrationKey);
      return migratedToken;
    }

    window.localStorage.setItem(key, encryptedValue);
    storage.removeItem(migrationKey);
    return token;
  } catch {
    try {
      if (window.localStorage.getItem(key) !== encryptedValue) {
        window.localStorage.setItem(key, encryptedValue);
      }
    } catch {
      return token;
    }

    try {
      storage.removeItem(migrationKey);
    } catch {
      return token;
    }

    return token;
  }
}

// Removes the current authentication token and any temporary migration value.
export function removeAccessToken() {
  storage.removeItem(key);
  storage.removeItem(migrationKey);
}

// Returns the current authentication token.
// v3 values are read directly; existing v2 values are detected, decrypted,
// migrated to v3, and then returned. Invalid or corrupted values are removed.
export function getAccessToken() {
  let encryptedValue: string | null;

  try {
    encryptedValue = window.localStorage.getItem(key);
  } catch {
    return undefined;
  }

  if (!encryptedValue) {
    return undefined;
  }

  try {
    const token = storage.getItem<AuthResponse>(key);
    if (isAuthResponse(token)) {
      return token;
    }
  } catch {}

  // v2 values use the OpenSSL/CryptoJS "Salted__" Base64 prefix.
  if (encryptedValue.startsWith("U2FsdGVkX1")) {
    const token = decryptV2Value(encryptedValue);
    if (token) {
      return migrateV2Value(token, encryptedValue);
    }
  }

  try {
    storage.removeItem(key);
  } catch {
    return undefined;
  }

  return undefined;
}

// Checks whether an authentication token is currently stored.
export function hasAccessToken() {
  return !!getAccessToken();
}

// Stores a new authentication token using the encrypt-storage v3 format.
export function setAccessToken(token: AuthResponse) {
  storage.setItem(key, token);
}

/** Refresh the token this many ms before it expires. */
const REFRESH_THRESHOLD_MS = 8 * 60 * 60 * 1000; // 8 hours

export function isTokenExpiringSoon(): boolean {
  const token = getAccessToken();
  if (!token?.access_token_expires) return false;
  const expiresAt = new Date(token.access_token_expires).getTime();
  return Date.now() >= expiresAt - REFRESH_THRESHOLD_MS;
}

/**
 * Calls POST /auth/refresh/ using the current bearer token and stores the
 * refreshed token. Uses raw fetch to avoid a circular dependency with the
 * Axios api-client.
 */
export async function refreshAccessToken(): Promise<void> {
  const current = getAccessToken();
  if (!current?.access_token) return;

  const response = await fetch(`${env.API_URL}/auth/refresh/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${current.access_token}`,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    removeAccessToken();
    throw new Error("Token refresh failed");
  }

  const data: AuthResponse = await response.json();
  setAccessToken({
    access_token: data.access_token,
    access_token_expires: data.access_token_expires,
    hostname: data.hostname ?? current.hostname,
  });
}

export const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const location = useLocation();
  const { isLoading: isPlansLoading } = usePlansUser();

  if (user.isFetching || user.isLoading || user.isPending || isPlansLoading) {
    return <Skeleton h="100vh" />;
  }

  if (!getAccessToken() || !user.data) {
    return <Navigate to={paths.auth.login.getHref(location.pathname)} replace />;
  }

  return children;
};

export const ProtectedOnboardingRoute: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const location = useLocation();
  const {
    /* data: plans, */
    isLoading: isPlansLoading,
  } = usePlansUser();

  if (user.isFetching || user.isLoading || user.isPending || isPlansLoading) {
    return <Skeleton h="100vh" />;
  }

  if (!getAccessToken() || !user.data) {
    return <Navigate to={paths.auth.login.getHref(location.pathname)} replace />;
  }

  /* const u = user.data as any; */

  /*   const onboardingCompleted =
    u?.additional_info?.onboarding_completed === true ||
    (u?.additional_info?.onboarding && Object.keys(u.additional_info.onboarding).length > 0);

  const planSelected = !!plans?.name; */

  // If onboarding not completed or the user has no selected plan, redirect to /plans
  /*   if (!onboardingCompleted || !planSelected) {
    return <Navigate to={paths.plans.root.getHref()} replace />;
  } */

  return children;
};
